/**
 * 腾讯广告 → T0广告看板（CLAUDE.md §前端 UI 须借鉴 LEO；本页复用 leo-* 类）。
 *
 * 已读 leo-frontend-design.md v1.0，适用章节：§4.2 滚动内容页（A 模式）、
 * §6.1 按钮层级、§6.2 导航（Tab 选中态：橙 tint + 橙字）、§6.3 表单控件、
 * §6.4 卡片、§6.5 表格/列表（表头 L4 muted、行分隔细线、禁止浅色斑马纹）、
 * §6.6 弹窗与浮层（D 类确认框）、§7.2 反馈原则（Toast / 加载中）、§7.3 空值 —。
 *
 * 页面结构（自上而下）：
 *   1. 自定义账户集合卡片（CRUD；可编辑组 page_key=tencent:t0-dashboard）；
 *   2. 筛选条：代理商（必选，默认「新客大盘」= 全部名称解析代理汇总、排除
 *      自定义账户集合行——2026-10-26 需求，原为第一个非 YK/YR/PR 值）/
 *      账户ID（输入，空=全部）/ 广告ID（输入，空=全部）/ 时间节点（必选；
 *      2026-10-30 起由分钟级单一下拉改为 天日期/小时/分钟 三级级联——
 *      省市区式逐级出现，天日期为日历选择界面（DateCalendarPicker，仅
 *      有数据日期可点，白名单外禁用），三级选项均由表内实际 data_time
 *      派生（最近 7 天），默认 天日期=当天、小时=当前小时、分钟="03"，
 *      缺值按 最新日/最大小时/最早分钟 兜底；选项数据口径自 2026-10-23
 *      起圈定表内最新天起最近 7 天）；
 *      账户ID / 广告ID 经 Enter / 失焦 / 「查询」按钮
 *      （级联分钟右侧，2026-10-26 需求）应用后刷新数据；
 *   2026-10-26 UI 优化：画布 max-w 1280→1664px（+30%）；「自定义账户集合」与
 *   筛选条（代理商/账户ID/广告ID/时间节点）两区域字体整体增大 1 号（leo-*
 *   共享控件经 index.css `.t0-board-font-lg` 作用域覆盖，见该文件尾注）。
 *   3. 数据区：「数据合计」「数据详情」上下分区块展示（2026-10-22 起，不再分 Tab）：
 *      - 数据合计：11 个指标分 2 行，指标名在上、数值在下，灰线框包裹；
 *        比例类指标百分比展示，数值类指标千分位整数（T0企业进件成本 / T0企业件均 /
 *        消耗(元) / T0企业授信金额 按需求只展示整数部分），均加粗加大
 *        （26px ≈ 标签 13px 的 2 倍）；
 *      - 数据详情：标题旁「账户粒度 / 广告粒度」tab（默认账户粒度，
 *        2026-10-26 需求；两粒度后端同包返回，tab 纯本地切换）：
 *        账户粒度 = 12 列（账户ID + 11 指标，对该账户下全部广告聚合）；
 *        广告粒度 = 14 列明细表。表头置顶（sticky），默认按消耗降序（后端序）；
 *        2026-10-23 起 9 个指标列（T0企业费用率 / T0企业进件成本 / T0企业件均 /
 *        消耗(元) / T0企业进件量 / T0企业授信量 / T0企业授信金额 / 完件数(回传口径) /
 *        留资数(回传口径)）支持表头点击前端本地排序（未排 → 升序 → 降序 → 复原，
 *        空值「—」恒沉底，两粒度共用同一排序状态；
 *        T0企业通过率 / 留资-完件率(回传口径) 按需求不排序）。
 *
 * 数据源：tx_t0_ad_board_latest（auto_strategy 第 5 跳分钟级快照写入）；
 * 合计比率为 SUM(x)/SUM(y) 合计相除口径（非逐行比率求均值）。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type CustomAccountSetRecord,
  createCustomAccountSet,
  deleteCustomAccountSet,
  listCustomAccountSets,
  updateCustomAccountSet,
} from "../../services/tencent/customAccountSet";
import {
  T0_AD_BOARD_ALL_DAILI,
  type T0AdBoardAccountRow,
  type T0AdBoardDetailRow,
  type T0AdBoardSummary,
} from "../../services/tencent/t0AdBoard";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_KEYS } from "../../config/permissions";
import { pushToast } from "../../hooks/useToast";
import { useT0AdBoardData } from "../../hooks/useT0AdBoard";
import DateCalendarPicker from "../../components/DateCalendarPicker";

/** 集合名称最大长度（与后端 SET_NAME_MAX_LEN 一致，需求：20 字以内）。 */
const SET_NAME_MAX_LEN = 20;

/** 指标数值类型：percent=比例（百分比展示）/ int=数值（千分位整数，金额类同样只展示整数部分）。 */
type MetricKind = "percent" | "int";

/** 指标展示配置（合计与详情共用同一顺序与口径）。 */
interface MetricDef {
  key: keyof T0AdBoardSummary;
  label: string;
  kind: MetricKind;
  /** 数据详情列是否支持表头点击排序（2026-10-23 需求给定 9 列可排序）。 */
  sortable?: boolean;
}

/** 11 个指标（需求给定顺序；前 6 个为第一行，后 5 个为第二行）。
 *  sortable 标记数据详情可排序列（T0企业通过率 / 留资-完件率 按需求不排序）。 */
const SUMMARY_METRICS: MetricDef[] = [
  { key: "t0_qy_feilv", label: "T0企业费用率", kind: "percent", sortable: true },
  { key: "t0_qyjj_per", label: "T0企业进件成本", kind: "int", sortable: true },
  { key: "t0_credit_ratio", label: "T0企业通过率", kind: "percent" },
  { key: "t0_credit_per_amt", label: "T0企业件均", kind: "int", sortable: true },
  { key: "cost", label: "消耗(元)", kind: "int", sortable: true },
  { key: "t0_qyjj", label: "T0企业进件量", kind: "int", sortable: true },
  { key: "t0_qy_credit", label: "T0企业授信量", kind: "int", sortable: true },
  { key: "t0_qy_credit_amt", label: "T0企业授信金额", kind: "int", sortable: true },
  { key: "apply_pv", label: "完件数(回传口径)", kind: "int", sortable: true },
  { key: "reservation_uv", label: "留资数(回传口径)", kind: "int", sortable: true },
  { key: "resv_apply_ratio", label: "留资-完件率(回传口径)", kind: "percent" },
];

const SUMMARY_ROW1 = SUMMARY_METRICS.slice(0, 6);
const SUMMARY_ROW2 = SUMMARY_METRICS.slice(6);

/** 指标值格式化：空值 —（LEO §7.3）；比例 → 百分比；数值 → 千分位整数
 * （T0企业进件成本 / T0企业件均 / 消耗(元) / T0企业授信金额 按需求只展示整数部分）。 */
function formatMetric(value: number | null, kind: MetricKind): string {
  if (value === null || value === undefined) return "—";
  if (kind === "percent") return `${(value * 100).toFixed(2)}%`;
  return Math.round(value).toLocaleString("en-US");
}

/** 明细行前端本地排序（广告 / 账户两粒度共用同 11 个指标键）：未排返回原序
 * （后端 cost 降序）；空值「—」恒沉底，与排序方向无关。 */
function sortMetricRows<T extends Record<keyof T0AdBoardSummary, number | null>>(
  rows: T[],
  sortKey: keyof T0AdBoardSummary | null,
  sortDir: "asc" | "desc",
): T[] {
  if (sortKey === null) return rows;
  const dir = sortDir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = a[sortKey];
    const vb = b[sortKey];
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return (va - vb) * dir;
  });
}

/**
 * 账户 ID 文本 → 字符串数组：按 逗号/中文逗号/空白/换行 分隔，
 * 逐元素 strip、剔空、纯数字校验、去重保序；非法时返回错误文案。
 */
function parseAccountIds(text: string): { ids: string[]; error: string | null } {
  const parts = text.split(/[,，\s]+/);
  const ids: string[] = [];
  for (const raw of parts) {
    const aid = raw.trim();
    if (!aid) continue;
    if (!/^\d+$/.test(aid)) {
      return { ids: [], error: `账户 ID 须为纯数字：${aid}` };
    }
    if (!ids.includes(aid)) ids.push(aid);
  }
  if (ids.length === 0) {
    return { ids: [], error: "账户集合不能为空，请输入至少 1 个账户 ID" };
  }
  return { ids, error: null };
}

/** 数据合计·单指标卡片：指标名在上、数值在下，灰线框包裹（数值 26px 加粗 ≈ 标签 13px 的 2 倍）。
 *  指标名称用蓝色（LEO §2.1 辅色蓝 accent.blue #3b82f6，静态 token 深浅主题通用）。 */
function MetricBox({ label, text }: { label: string; text: string }) {
  return (
    <div className="flex min-w-[150px] flex-1 flex-col gap-1.5 rounded-lg border border-line-default bg-da-inset px-3 py-2.5">
      <span className="text-[11px] text-accent-blue">{label}</span>
      <span className="text-[26px] font-bold leading-tight text-text-emphasis">
        {text}
      </span>
    </div>
  );
}

export default function T0DashboardPage() {
  const { canEdit } = useAuth();
  const editable = canEdit(PAGE_KEYS.TENCENT_T0_DASHBOARD);

  const [sets, setSets] = useState<CustomAccountSetRecord[] | null>(null);
  /** 是否展开新建表单。 */
  const [creating, setCreating] = useState(false);
  /** 行内编辑目标（null = 无编辑中）。 */
  const [editingId, setEditingId] = useState<number | null>(null);
  /** 表单字段（新建 / 编辑共用：进入编辑时回填）。 */
  const [formName, setFormName] = useState("");
  const [formIdsText, setFormIdsText] = useState("");
  /** 删除确认目标（LEO §6.6 D 类确认框；null = 不展示）。 */
  const [deleting, setDeleting] = useState<CustomAccountSetRecord | null>(null);
  const [saving, setSaving] = useState(false);

  // 看板数据（选项 + 已应用筛选 + 合计/明细 + 时间节点级联），数据获取封装于 Hook。
  const {
    options,
    filters,
    setFilters,
    data,
    loading,
    timeCascade,
    timeParts,
    applyTimePart,
  } = useT0AdBoardData();
  /** 账户ID / 广告ID 输入框的输入态（Enter / 失焦后才应用进 filters）。 */
  const [accountInput, setAccountInput] = useState("");
  const [adInput, setAdInput] = useState("");
  /** 数据详情排序列（null = 恢复后端 cost 降序原序）。 */
  const [sortKey, setSortKey] = useState<keyof T0AdBoardSummary | null>(null);
  /** 排序方向（sortKey 为 null 时无意义）。 */
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  /** 数据详情粒度 tab（2026-10-26 需求：默认账户粒度；切换为纯本地，不重新请求）。 */
  const [detailTab, setDetailTab] = useState<"account" | "ad">("account");

  /** 排序后的明细行（广告 / 账户两粒度共用同一排序状态；空值「—」恒沉底）。 */
  const sortedAdRows: T0AdBoardDetailRow[] = useMemo(
    () => sortMetricRows(data?.rows ?? [], sortKey, sortDir),
    [data, sortKey, sortDir],
  );
  const sortedAccountRows: T0AdBoardAccountRow[] = useMemo(
    () => sortMetricRows(data?.account_rows ?? [], sortKey, sortDir),
    [data, sortKey, sortDir],
  );

  /** 表头点击排序循环：未排 → 升序 → 降序 → 取消（恢复后端 cost 降序原序）。 */
  function onSortHeader(key: keyof T0AdBoardSummary) {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
  }

  const reload = useCallback(async () => {
    try {
      setSets(await listCustomAccountSets());
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "拉取自定义账户集合失败";
      pushToast(msg, "error");
      setSets([]);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** 应用文本筛选（trim；空串 = 全部）。 */
  function applyTextFilter(key: "account_id" | "ad_id", raw: string) {
    const v = raw.trim();
    setFilters((prev) =>
      prev && prev[key] !== v ? { ...prev, [key]: v } : prev,
    );
  }

  /** 「查询」按钮（2026-10-26 需求，位于时间节点右侧）：将 账户ID / 广告ID
   *  输入态一并应用进 filters 触发刷新（与 Enter / 失焦同效；值未变时不发请求）。 */
  function onQuery() {
    setFilters((prev) => {
      if (!prev) return prev;
      const account_id = accountInput.trim();
      const ad_id = adInput.trim();
      if (prev.account_id === account_id && prev.ad_id === ad_id) return prev;
      return { ...prev, account_id, ad_id };
    });
  }

  function startCreate() {
    setCreating(true);
    setEditingId(null);
    setFormName("");
    setFormIdsText("");
  }

  function startEdit(item: CustomAccountSetRecord) {
    setEditingId(item.id);
    setCreating(false);
    setFormName(item.set_name);
    setFormIdsText(item.account_ids.join(", "));
  }

  function cancelForm() {
    setCreating(false);
    setEditingId(null);
  }

  /** 表单校验：名称 1~20 字 + 账户列表解析；不合法 toast 报错且不发请求。 */
  function validateForm(): { set_name: string; account_ids: string[] } | null {
    const name = formName.trim();
    if (!name) {
      pushToast("集合名称不能为空", "error");
      return null;
    }
    if (name.length > SET_NAME_MAX_LEN) {
      pushToast(`集合名称最长 ${SET_NAME_MAX_LEN} 字`, "error");
      return null;
    }
    // 保留名校验（与后端 DTO 一致）：同名集合会被代理商筛选的虚拟汇总项遮蔽
    if (name === T0_AD_BOARD_ALL_DAILI) {
      pushToast(`集合名称不可用保留名「${T0_AD_BOARD_ALL_DAILI}」`, "error");
      return null;
    }
    const { ids, error } = parseAccountIds(formIdsText);
    if (error) {
      pushToast(error, "error");
      return null;
    }
    return { set_name: name, account_ids: ids };
  }

  async function onSubmitCreate() {
    const body = validateForm();
    if (!body) return;
    setSaving(true);
    try {
      setSets(await createCustomAccountSet(body));
      pushToast("集合已新建", "success");
      cancelForm();
    } catch (err) {
      pushToast(err instanceof ApiError ? err.message : "新建集合失败", "error");
    } finally {
      setSaving(false);
    }
  }

  async function onUpdate() {
    if (editingId === null) return;
    const body = validateForm();
    if (!body) return;
    setSaving(true);
    try {
      setSets(await updateCustomAccountSet(editingId, body));
      pushToast("集合已保存", "success");
      cancelForm();
    } catch (err) {
      pushToast(err instanceof ApiError ? err.message : "保存集合失败", "error");
    } finally {
      setSaving(false);
    }
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    setSaving(true);
    try {
      setSets(await deleteCustomAccountSet(deleting.id));
      pushToast(`集合「${deleting.set_name}」已删除`, "success");
      setDeleting(null);
    } catch (err) {
      pushToast(err instanceof ApiError ? err.message : "删除集合失败", "error");
    } finally {
      setSaving(false);
    }
  }

  /** 集合名称 + 账户列表表单（新建 / 编辑共用）。 */
  function renderForm(submitLabel: string, onSubmit: () => void) {
    return (
      <div className="mt-3 rounded-lg border border-line-default bg-da-inset p-3">
        <div className="flex flex-col gap-1">
          <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
            自定义账户集合名称（{SET_NAME_MAX_LEN} 字以内）
          </label>
          <input
            type="text"
            className="leo-input"
            maxLength={SET_NAME_MAX_LEN}
            placeholder="如：周雅自投"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
          />
        </div>
        <div className="mt-2 flex flex-col gap-1">
          <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
            账户集合（逗号 / 换行分隔的账户 ID）
          </label>
          <textarea
            className="leo-textarea min-h-[72px] font-mono"
            placeholder={"91347885, 91347879\n91347874 ..."}
            value={formIdsText}
            onChange={(e) => setFormIdsText(e.target.value)}
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving}
            className="leo-cta-primary w-auto px-7"
          >
            {saving ? "保存中..." : submitLabel}
          </button>
          <button
            type="button"
            onClick={cancelForm}
            disabled={saving}
            className="leo-btn-ghost"
          >
            取消
          </button>
        </div>
      </div>
    );
  }

  /** 数据合计：11 指标分 2 行（6 + 5）；无匹配行（summary=null）时全部展示 —。 */
  function renderSummary(summary: T0AdBoardSummary | null) {
    const renderRow = (defs: MetricDef[]) => (
      <div className="flex flex-wrap gap-2">
        {defs.map((m) => (
          <MetricBox
            key={m.key}
            label={m.label}
            text={formatMetric(summary ? summary[m.key] : null, m.kind)}
          />
        ))}
      </div>
    );
    return (
      <div className="flex flex-col gap-2">
        {renderRow(SUMMARY_ROW1)}
        {renderRow(SUMMARY_ROW2)}
      </div>
    );
  }

  /** 指标列表头（广告 / 账户两粒度共用）：可排序列渲染点击按钮
   *  （未排 → 升序 → 降序 → 复原，空值「—」恒沉底）。 */
  function renderMetricTh(m: MetricDef) {
    return (
      <th
        key={m.key}
        className="border-b border-line-default bg-da-card px-3 py-2 text-right font-medium text-text-muted"
      >
        {m.sortable ? (
          <button
            type="button"
            onClick={() => onSortHeader(m.key)}
            title="点击排序：升序 → 降序 → 复原"
            className="inline-flex items-center gap-1 transition-colors hover:text-text-strong"
          >
            {m.label}
            <span
              className={
                sortKey === m.key ? "text-accent-cta" : "text-text-muted-weak"
              }
            >
              {sortKey === m.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
            </span>
          </button>
        ) : (
          m.label
        )}
      </th>
    );
  }

  /** 数据详情·广告粒度：14 列明细表（表头置顶 sticky；未排序时按消耗降序由后端排序）。 */
  function renderDetail(rows: T0AdBoardDetailRow[], isTruncated: boolean) {
    if (rows.length === 0) {
      return (
        <div className="flex min-h-[20vh] items-center justify-center">
          <div className="text-[13px] text-text-muted">当前筛选无数据</div>
        </div>
      );
    }
    return (
      <>
        {isTruncated && (
          <div className="mb-2 text-[11px] text-semantic-warning">
            仅展示前 {rows.length} 条（按消耗降序截取），请叠加 账户ID / 广告ID 筛选缩小范围
          </div>
        )}
        <div className="max-h-[62vh] overflow-auto rounded-lg border border-line-subtle">
          <table className="w-full border-collapse whitespace-nowrap text-[12px]">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="border-b border-line-default bg-da-card px-3 py-2 text-left font-medium text-text-muted">
                  账户ID
                </th>
                <th className="border-b border-line-default bg-da-card px-3 py-2 text-left font-medium text-text-muted">
                  广告ID
                </th>
                <th className="border-b border-line-default bg-da-card px-3 py-2 text-left font-medium text-text-muted">
                  广告名称
                </th>
                {SUMMARY_METRICS.map(renderMetricTh)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.account_id}-${r.ad_id}`}
                  className="border-b border-line-subtle text-text-strong"
                >
                  <td className="px-3 py-2 font-mono text-text-emphasis">
                    {r.account_id}
                  </td>
                  <td className="px-3 py-2 font-mono text-text-emphasis">
                    {r.ad_id}
                  </td>
                  <td className="px-3 py-2 text-text-body" title={r.adgroup_name}>
                    <div className="max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap">
                      {r.adgroup_name || "—"}
                    </div>
                  </td>
                  {SUMMARY_METRICS.map((m) => (
                    <td key={m.key} className="px-3 py-2 text-right font-mono">
                      {formatMetric(r[m.key], m.kind)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  /** 数据详情·账户粒度（2026-10-26 需求）：12 列（账户ID + 11 指标），
   *  对该账户下全部广告聚合（比率 = 合计相除）；排序与广告粒度共用同一状态。 */
  function renderAccountDetail(
    rows: T0AdBoardAccountRow[],
    isTruncated: boolean,
  ) {
    if (rows.length === 0) {
      return (
        <div className="flex min-h-[20vh] items-center justify-center">
          <div className="text-[13px] text-text-muted">当前筛选无数据</div>
        </div>
      );
    }
    return (
      <>
        {isTruncated && (
          <div className="mb-2 text-[11px] text-semantic-warning">
            仅展示前 {rows.length} 个账户（按消耗降序截取），请叠加 账户ID 筛选缩小范围
          </div>
        )}
        <div className="max-h-[62vh] overflow-auto rounded-lg border border-line-subtle">
          <table className="w-full border-collapse whitespace-nowrap text-[12px]">
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="border-b border-line-default bg-da-card px-3 py-2 text-left font-medium text-text-muted">
                  账户ID
                </th>
                {SUMMARY_METRICS.map(renderMetricTh)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.account_id}
                  className="border-b border-line-subtle text-text-strong"
                >
                  <td className="px-3 py-2 font-mono text-text-emphasis">
                    {r.account_id}
                  </td>
                  {SUMMARY_METRICS.map((m) => (
                    <td key={m.key} className="px-3 py-2 text-right font-mono">
                      {formatMetric(r[m.key], m.kind)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  const hasOptions =
    !!options && options.daili_list.length > 0 && options.data_time_list.length > 0;

  return (
    // 画布 max-w 1280→1664px（+30%，2026-10-26 需求）
    <div className="mx-auto max-w-[1664px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5">
        <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
          T0广告看板
        </h1>
        <p className="mt-1 text-[12.5px] text-text-muted">
          腾讯广告 T0 业绩看板（tx_t0_ad_board_latest，分钟级时点快照）；
          自定义账户集合可按名称圈选账户组，作为「代理商」筛选口径之一。
        </p>
      </div>

      {/* 自定义账户集合（置于筛选条上方）；t0-board-font-lg 供 index.css 作用域字号覆盖 */}
      <div className="t0-board-font-lg leo-card mb-4 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[14.5px] font-bold text-text-emphasis">
            自定义账户集合
          </h2>
          {editable && !creating && (
            <button
              type="button"
              onClick={startCreate}
              className="leo-btn-ghost"
            >
              + 新建集合
            </button>
          )}
        </div>

        {creating && renderForm("新建", onSubmitCreate)}

        {sets === null ? (
          <div className="flex min-h-[12vh] items-center justify-center">
            <div className="text-[14px] text-text-muted">加载中…</div>
          </div>
        ) : sets.length === 0 && !creating ? (
          <div className="flex min-h-[12vh] items-center justify-center">
            <div className="text-[14px] text-text-muted">暂无自定义账户集合</div>
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            {sets.map((item) => (
              <div
                key={item.id}
                className="rounded-lg border border-line-subtle px-3 py-2.5"
              >
                {editingId === item.id ? (
                  renderForm("保存", onUpdate)
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-semibold text-text-emphasis">
                          {item.set_name}
                        </span>
                        <span className="text-[12px] text-text-muted-weak">
                          {item.account_ids.length} 个账户
                        </span>
                      </div>
                      {editable && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            className="leo-btn-ghost px-2.5 py-1"
                          >
                            编辑
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleting(item)}
                            className="leo-btn-ghost is-danger px-2.5 py-1"
                          >
                            删除
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {item.account_ids.map((aid) => (
                        <span key={aid} className="leo-tag font-mono">
                          {aid}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 筛选条：代理商 / 时间节点必选（无「全部」）；账户ID / 广告ID 输入，空=全部；
          t0-board-font-lg 供 index.css 作用域字号覆盖 */}
      <div className="t0-board-font-lg leo-card mb-4 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
              代理商
            </label>
            <select
              className="leo-select w-[170px]"
              value={filters?.daili ?? ""}
              disabled={!hasOptions}
              onChange={(e) =>
                setFilters((prev) =>
                  prev ? { ...prev, daili: e.target.value } : prev,
                )
              }
            >
              {!hasOptions && <option value="">暂无数据</option>}
              {options?.daili_list.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
              账户ID
            </label>
            <input
              type="text"
              className="leo-input w-[150px]"
              placeholder="全部"
              value={accountInput}
              onChange={(e) => setAccountInput(e.target.value)}
              onBlur={(e) => applyTextFilter("account_id", e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyTextFilter("account_id", accountInput);
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
              广告ID
            </label>
            <input
              type="text"
              className="leo-input w-[180px]"
              placeholder="全部"
              value={adInput}
              onChange={(e) => setAdInput(e.target.value)}
              onBlur={(e) => applyTextFilter("ad_id", e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyTextFilter("ad_id", adInput);
              }}
            />
          </div>
          {/* 时间节点级联（2026-10-30 需求，省市区式逐级出现）：
              天日期（必选，默认当天；日历选择界面，白名单外无数据日期禁用）
              → 选择后出现 小时（默认当前小时）→ 分钟（默认 "03"）；
              三级选项均由表内实际 data_time 派生，
              改父级时子级按默认规则重取（hook 内 applyTimePart） */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
              天日期
            </label>
            <DateCalendarPicker
              value={timeParts.ds}
              allowedDates={timeCascade.dsList}
              disabled={!hasOptions}
              onChange={(ds) => applyTimePart("ds", ds)}
            />
          </div>
          {timeParts.ds && (
            <div className="flex flex-col gap-1">
              <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
                小时
              </label>
              <select
                className="leo-select w-[90px] font-mono"
                value={timeParts.hh}
                disabled={!hasOptions}
                onChange={(e) => applyTimePart("hh", e.target.value)}
              >
                {timeCascade.hourList.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>
          )}
          {timeParts.ds && timeParts.hh && (
            <div className="flex flex-col gap-1">
              <label className="text-[12px] uppercase tracking-[0.05em] text-text-muted-weak">
                分钟
              </label>
              <select
                className="leo-select w-[90px] font-mono"
                value={timeParts.mm}
                disabled={!hasOptions}
                onChange={(e) => applyTimePart("mm", e.target.value)}
              >
                {timeCascade.minuteList.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* 查询按钮（2026-10-26 需求，级联分钟右侧；items-end 底对齐）：
              点击将 账户ID / 广告ID 输入态一并应用进 filters 刷新数据 */}
          <button
            type="button"
            onClick={onQuery}
            disabled={!hasOptions}
            className="leo-cta-primary w-auto px-7"
          >
            查询
          </button>
        </div>
      </div>

      {/* 数据区：数据合计（上）/ 数据详情（下）分区块展示（不分 Tab） */}
      {!hasOptions ? (
        <div className="leo-card rounded-xl p-4">
          <div className="flex min-h-[20vh] items-center justify-center">
            <div className="text-[13px] text-text-muted">
              {options === null
                ? "加载中…"
                : "暂无看板数据（待 auto_strategy 第 5 跳写入 tx_t0_ad_board_latest）"}
            </div>
          </div>
        </div>
      ) : loading && !data ? (
        <div className="leo-card rounded-xl p-4">
          <div className="flex min-h-[20vh] items-center justify-center">
            <div className="text-[13px] text-text-muted">加载中…</div>
          </div>
        </div>
      ) : (
        <>
          <div className="leo-card mb-4 rounded-xl p-4">
            <h2 className="mb-3 text-[13.5px] font-bold text-text-emphasis">
              数据合计
            </h2>
            {renderSummary(data?.summary ?? null)}
          </div>
          <div className="leo-card rounded-xl p-4">
            <div className="mb-3 flex items-center gap-3">
              <h2 className="text-[13.5px] font-bold text-text-emphasis">
                数据详情
              </h2>
              {/* 粒度切换 tab（2026-10-26 需求，默认账户粒度，紧邻标题右侧；
                  LEO §6.2 leo-toptab：选中态橙 tint + 橙字；
                  两粒度后端同包返回，切换纯本地不请求） */}
              <div className="flex items-center gap-1" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={detailTab === "account"}
                  onClick={() => setDetailTab("account")}
                  className={`leo-toptab ${detailTab === "account" ? "is-active" : ""}`}
                >
                  账户粒度
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={detailTab === "ad"}
                  onClick={() => setDetailTab("ad")}
                  className={`leo-toptab ${detailTab === "ad" ? "is-active" : ""}`}
                >
                  广告粒度
                </button>
              </div>
            </div>
            {detailTab === "account"
              ? renderAccountDetail(
                  sortedAccountRows,
                  data?.is_account_truncated ?? false,
                )
              : renderDetail(sortedAdRows, data?.is_truncated ?? false)}
          </div>
        </>
      )}

      {/* 删除确认框（LEO §6.6 D 类：遮罩 65% + blur；取消灰钮 + 确认青描边） */}
      {deleting && (
        <div
          className="fixed inset-0 z-[11000] flex items-center justify-center bg-[rgb(2,6,23)]/65 p-4 backdrop-blur-[4px]"
          onClick={() => !saving && setDeleting(null)}
        >
          <div
            className="leo-card w-full max-w-[440px] rounded-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[14px] font-bold text-text-emphasis">删除集合</h3>
            <p className="mt-2 text-[12.5px] leading-relaxed text-text-body">
              确认删除自定义账户集合「{deleting.set_name}」？
              删除后不再展示（软删除，可随时由管理员恢复）。
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleting(null)}
                disabled={saving}
                className="leo-btn-ghost"
              >
                取消
              </button>
              <button
                type="button"
                onClick={onConfirmDelete}
                disabled={saving}
                className="rounded-lg border border-cyan-400/45 px-4 py-2 text-[12px] font-medium text-cyan-200 transition-colors hover:bg-cyan-400/10 disabled:opacity-55"
              >
                {saving ? "删除中..." : "确认删除"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
